import React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { IconQuestionCircle, IconReload, IconTimes } from '../../icons/library'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { IconHelp } from '../../icons/help'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { DropTarget } from '../../components/media/dropTarget'
import { FloatingLoader } from '../../components/simple/loader'

const sizeClass = {
  xs: 'sm:min-w-[250px] sm:max-w-[350px] flex-grow sm:flex-grow-0',
  sm: 'sm:min-w-[350px] sm:max-w-[450px] flex-grow sm:flex-grow-0',
  md: 'sm:min-w-[500px] flex-grow sm:flex-grow-0',
  lg: 'sm:min-w-[600px] flex-grow sm:flex-grow-0',
  xl: 'sm:min-w-[700px] flex-grow sm:flex-grow-0',
}

interface ModalTitleProps {
  children: React.ReactNode
  className?: string
  hidden?: boolean
}

/**
 * A title component for the modal. This is a wrapper around Radix Dialog.Title
 * that adds a description and handles hiding the title when not needed.
 *
 * A modal title is required by Radix, so if you don't want a title, use the hidden prop.
 */
export const ModalTitle: React.FC<ModalTitleProps> = ({ children, className, hidden }) => {
  const title = (
    <Dialog.Title className={className}>
      <Dialog.Description>{children}</Dialog.Description>
    </Dialog.Title>
  )
  return hidden ? <VisuallyHidden>{title}</VisuallyHidden> : title
}
interface ModalProps {
  /** The content of the modal */
  children?: React.ReactNode
  /** The title of the modal. Radix complains if this is not provided, so use a hidden <ModalTitle> if omitting */
  title?: React.ReactNode
  /** Whether the modal is open or closed */
  open: boolean
  /** Callback when the modal is opened or closed */
  onOpenChange: (open: boolean) => void
  /** The size of the modal. See sizeClass for what each size means */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** A link to a help page, which is shown as a question mark icon in the top right */
  help?: string
  /** Info tooltip shown as a question mark icon in the top right */
  info?: string
  /** Whether the modal close button is shown */
  closeable?: boolean
  /** Whether the modal is centered vertically */
  centered?: boolean
  /** Whether the modal is disabled, which makes it unclickable and grays out the content */
  disabled?: boolean
  /** Additional class names to add to the modal */
  className?: string
  /** Configures a reload button in the top right corner of the modal, which triggers the provided callback on click */
  reload?: { fn: () => void; flag?: string }
  /** An id for the modal, used for accessibility and tutorials */
  id?: string
  /** Configures a drop target for the modal, which allows files to be dropped onto it */
  dropTarget?: {
    onDrop: (files: File[]) => void
    canDrop: () => boolean
  }
  /** If true, the modal will not be focused when opened. */
  noAutoFocus?: boolean
  /** If true, the modal will show a loading spinner over the content. */
  showLoader?: boolean
  /** Additional class names to add to the modal overlay */
  overlayClassName?: string
}

export const modalContent = 'relative m-2 flex flex-col rounded-2xl bg-white p-4 shadow-lg focus:outline-none'
const modalAnim = 'data-[state=closed]:animate-modalContentHide data-[state=open]:animate-modalContentShow'

/**
 * A controlled modal component that uses Radix Dialog to create a portaled modal.
 * It supports a variety of features, including a title, help link, info tooltip,
 * close button, reload button, and drop target for files.
 */
export const ControlledModal: React.FC<ModalProps> = ({
  children,
  title,
  open,
  onOpenChange,
  help,
  info,
  size = 'lg',
  closeable = true,
  centered = false,
  disabled = false,
  className = '',
  reload,
  id,
  dropTarget,
  noAutoFocus,
  showLoader,
  overlayClassName = '',
}) => {
  const modalContent = 'min-w-0 relative m-2 flex flex-col gap-2 rounded-2xl bg-white p-4 shadow-lg focus:outline-none'
  const modalAnim = 'data-[state=closed]:animate-modalContentHide data-[state=open]:animate-modalContentShow'

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* We want to use Dialog.Overlay here, but see https://github.com/shadcn-ui/ui/issues/5919 */}
        <div
          className={`pointer-events-auto fixed inset-0 z-[1000] ${overlayClassName} flex ${centered ? 'items-center' : 'items-start'} justify-center overflow-auto bg-slate-500/25 data-[state=closed]:animate-modalOverlayHide data-[state=open]:animate-modalOverlayShow`}
        >
          <Dialog.Content
            asChild
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => noAutoFocus && e.preventDefault()}
          >
            <DropTarget
              id={id}
              className={`${className} ${modalContent} ${modalAnim} ${centered ? 'mb-48' : 'mt-16'} ${sizeClass[size]} ${disabled ? '!pointer-events-none *:opacity-50' : ''}`}
              onDrop={dropTarget?.onDrop}
              canDrop={dropTarget?.canDrop}
            >
              {title && <ModalTitle className="mr-4 text-xl font-bold">{title}</ModalTitle>}
              {help && (
                <a href={help} target="_blank" className={`absolute right-9 top-1 ${ButtonStyle.close}`}>
                  <IconHelp />
                </a>
              )}
              {info && (
                <Button
                  buttonStyle={ButtonStyle.close}
                  shape={ButtonShape.close}
                  className={`absolute right-9 top-1 !cursor-default`}
                  tooltip={info}
                >
                  <IconQuestionCircle size="lg" />
                </Button>
              )}
              {reload && (
                <>
                  <Button
                    buttonStyle={ButtonStyle.close}
                    shape={ButtonShape.close}
                    className={`absolute right-9 top-1`}
                    onClick={reload.fn}
                  >
                    <IconReload />
                  </Button>
                  {reload.flag && (
                    <div className="absolute right-16 top-2 bg-warning pl-2 pr-8 [clip-path:polygon(96%_0,99%_50%,96%_100%,0_100%,0_0)]">
                      {reload.flag}
                    </div>
                  )}
                </>
              )}
              {closeable && (
                <Dialog.Close className={`pointer-events-auto absolute right-1 top-1 z-10 ${ButtonStyle.close}`}>
                  <IconTimes size="sm" thicken={40} />
                </Dialog.Close>
              )}
              {children}
              {showLoader && <FloatingLoader className="!opacity-100" />}
              {!children && !title && showLoader && <ModalTitle hidden>Loading...</ModalTitle>}
            </DropTarget>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
